import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { WorkspaceNotInitializedError } from '@/error-handler/WorkspaceNotInitializedError';

type State = {
  workspaceMap: Record<string, WorkspaceCacheState>;
  sidebarShrinked: boolean;
};

export type WorkspaceCacheState = {
  recentDashboardHistory: string[];
  feedbackDetailShowAll: boolean;
  homeOnboardingGuide: {
    import_feedback: boolean;
    create_taxonomy: boolean;
  };
};

type Actions = {
  setState: StoreSetter<State>;
  initWorkspaceCacheStore: () => void;
};

const initialState: State = {
  workspaceMap: {},
  sidebarShrinked: false,
};

const initialWorkspaceCahceState: WorkspaceCacheState = {
  recentDashboardHistory: [],
  feedbackDetailShowAll: false,
  homeOnboardingGuide: {
    import_feedback: true,
    create_taxonomy: true,
  },
};

const useWorkspaceCacheStore = create(
  persist<State & Actions>(
    (set, get) => ({
      ...initialState,
      setState: set,
      initWorkspaceCacheStore: () => set(initialState),
    }),
    {
      name: 'workspace-cache',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

const useWorkspaceCacheStoreAction = () => {
  const { workspace_id } = useParams();
  const workspaceMap = useWorkspaceCacheStore((state) => state.workspaceMap);
  const setState = useWorkspaceCacheStore((state) => state.setState);
  if (!workspace_id) throw new WorkspaceNotInitializedError();

  const currWorkspaceCahce = useMemo(() => {
    const newWorkspaceMap = {
      ...workspaceMap,
    };
    const cache = newWorkspaceMap[workspace_id];
    if (!cache) return initialWorkspaceCahceState;

    return cache;
  }, [workspace_id, workspaceMap]);

  const setWorspaceCache = useCallback(
    (
      cache:
        | Partial<WorkspaceCacheState>
        | ((state: WorkspaceCacheState) => WorkspaceCacheState),
    ) => {
      if (typeof cache === 'function') {
        setState((prev) => {
          const newWorkspaceMap = { ...prev.workspaceMap };
          const prevCache =
            prev.workspaceMap[workspace_id] || initialWorkspaceCahceState;
          const newCache = cache(prevCache);

          return {
            workspaceMap: {
              ...newWorkspaceMap,
              [workspace_id]: newCache,
            },
          };
        });
      } else {
        setState((prev) => {
          const newWorkspaceMap = { ...prev.workspaceMap };
          const prevCache = prev.workspaceMap[workspace_id];
          if (!prevCache) {
            newWorkspaceMap[workspace_id] = {
              ...initialWorkspaceCahceState,
              ...cache,
            };
          } else {
            newWorkspaceMap[workspace_id] = {
              ...prevCache,
              ...cache,
            };
          }
          return {
            workspaceMap: newWorkspaceMap,
          };
        });
      }
    },
    [setState, workspace_id],
  );

  return {
    cache: currWorkspaceCahce,
    setCache: setWorspaceCache,
  };
};

const useRecentDashboardHistory = () => {
  const { cache, setCache } = useWorkspaceCacheStoreAction();
  const recentDashboardHistory = useMemo(
    () => cache.recentDashboardHistory,
    [cache.recentDashboardHistory],
  );

  const setDashboardHistory = useCallback(
    (dashboardAlias: string) => {
      const prev = { ...cache };
      let newHistories = [...prev.recentDashboardHistory];
      const targetIndex = newHistories.findIndex((d) => d === dashboardAlias);
      if (targetIndex === -1) {
        newHistories.unshift(dashboardAlias);
      } else {
        newHistories.splice(targetIndex, 1);
        newHistories.unshift(dashboardAlias);
      }

      newHistories = newHistories.slice(0, 5);
      setCache({
        recentDashboardHistory: newHistories,
      });
    },
    [cache, setCache],
  );

  const deleteDashboardHistory = useCallback(
    (dashboardAliases: string[]) => {
      setCache({
        recentDashboardHistory: cache.recentDashboardHistory.filter(
          (d) => !dashboardAliases.includes(d),
        ),
      });
    },
    [cache, setCache],
  );

  return {
    history: recentDashboardHistory,
    setHistory: setDashboardHistory,
    deleteHistory: deleteDashboardHistory,
  };
};

const useFeedbackDetailShowAll = () => {
  const { cache, setCache } = useWorkspaceCacheStoreAction();

  const feedbackDetailShowAll = useMemo(
    () => cache.feedbackDetailShowAll,
    [cache.feedbackDetailShowAll],
  );

  const setFeedbackDetailShowAll = useCallback(
    (status: boolean) => {
      setCache({
        feedbackDetailShowAll: status,
      });
    },
    [setCache],
  );

  return {
    showAll: feedbackDetailShowAll,
    setShowAll: setFeedbackDetailShowAll,
  };
};

const useSidebarShrinked = () => {
  const { sidebarShrinked, setState } = useWorkspaceCacheStore((state) => ({
    sidebarShrinked: state.sidebarShrinked,
    setState: state.setState,
  }));

  const setSidebarShrinked = useCallback(
    (status: boolean) => {
      setState({
        sidebarShrinked: status,
      });
    },
    [setState],
  );

  return { shrinked: sidebarShrinked, setShrinked: setSidebarShrinked };
};

const useHomeOnboardingGuide = () => {
  const { cache, setCache } = useWorkspaceCacheStoreAction();

  const homeOnboardingGuide = useMemo(
    () =>
      cache.homeOnboardingGuide ||
      initialWorkspaceCahceState.homeOnboardingGuide,
    [cache.homeOnboardingGuide],
  );

  const setHomeOnboardingGuide = useCallback(
    (
      state:
        | Partial<WorkspaceCacheState['homeOnboardingGuide']>
        | ((
            state: WorkspaceCacheState['homeOnboardingGuide'],
          ) => Partial<WorkspaceCacheState['homeOnboardingGuide']>),
    ) => {
      if (typeof state === 'function') {
        setCache((prev) => {
          return {
            ...prev,
            homeOnboardingGuide: {
              ...prev.homeOnboardingGuide,
              ...state(prev.homeOnboardingGuide),
            },
          };
        });
      } else {
        setCache((prev) => {
          return {
            ...prev,
            homeOnboardingGuide: { ...prev.homeOnboardingGuide, ...state },
          };
        });
      }
    },
    [setCache],
  );
  return {
    homeOnboardingGuide,
    setHomeOnboardingGuide,
  };
};

export {
  useFeedbackDetailShowAll,
  useHomeOnboardingGuide,
  useRecentDashboardHistory,
  useSidebarShrinked,
  useWorkspaceCacheStoreAction,
};

export default useWorkspaceCacheStore;
